import {Avatar, Flag} from 'platform/components';
import {HStack, Show, Text} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerAdvancedSearchResultResponseBody} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps} from 'shared/web';

import {useAddress} from '../../../hooks/useAddress';
import {getMatchTextColor} from '../utils/getMatchTextColor';
import {joinInfoLine} from '../utils/joinInfoLine';
import {ResultRowShell} from './ResultRowShell';
import {RowContactAside} from './RowContactAside';
import {RowPrimaryContact} from './RowPrimaryContact';

interface BusinessResultRowProps extends TestIdProps {
  customer: CustomerAdvancedSearchResultResponseBody;
  onSelect: (customer: CustomerAdvancedSearchResultResponseBody, contactId?: string) => void;
  hidePrimaryContact?: boolean;
}

export function BusinessResultRow(props: BusinessResultRowProps) {
  const {composeAddress} = useAddress();
  const founder = props.customer.foundingPerson;

  const vatLabel = props.customer.vatNumber
    ? `${i18n.t('entity.businessInfo.labels.vatNumber')}: ${props.customer.vatNumber}`
    : null;
  const registrationLabel = props.customer.registrationNumber
    ? `${i18n.t('entity.invoice.labels.registrationNumber')}: ${props.customer.registrationNumber}`
    : null;
  const secondaryLine = joinInfoLine([
    vatLabel,
    registrationLabel,
    composeAddress(props.customer.businessAddress),
  ]);

  return (
    <ResultRowShell
      avatar={<Avatar icon="social/domain" size="small" />}
      aside={
        <RowContactAside
          phone={founder?.phone}
          phoneMatchType={founder?.phoneMatchType}
          email={founder?.email}
        />
      }
      onSelect={() => props.onSelect(props.customer)}
      data-testid={props['data-testid']}
    >
      <HStack spacing={2} align="center">
        <Show when={isNotNilOrEmpty(props.customer.tradeName)}>
          <Text
            size="small"
            color={getMatchTextColor(props.customer.tradeNameMatchType)}
            alternative
          >
            {props.customer.tradeName}
          </Text>
        </Show>
        <Flag label={i18n.t('general.labels.company')} colorScheme="purple" size="small" />
      </HStack>
      <Show when={isNotNilOrEmpty(secondaryLine)}>
        <Text size="xSmall" color="tertiary" noWrap>
          {secondaryLine}
        </Text>
      </Show>

      <Show when={!props.hidePrimaryContact && props.customer.primaryContact}>
        <RowPrimaryContact contact={props.customer.primaryContact} />
      </Show>
    </ResultRowShell>
  );
}
